/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { PdfRect } from './pdf-studio.models';

/** One piece of text as pdf.js reports it: the string and the box it sits in, in PDF points. */
export interface TextRun {
  str: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** The line ends after this run. */
  eol?: boolean;
}

/** A page's runs as one string, remembering where each run begins in it. */
export interface PageText {
  text: string;
  spans: { start: number; end: number; run: TextRun }[];
}

/** Joins a page's runs in reading order, a newline wherever a line ends. */
export function layoutText(runs: readonly TextRun[]): PageText {
  const spans: PageText['spans'] = [];
  let text = '';
  for (const run of runs) {
    spans.push({ start: text.length, end: text.length + run.str.length, run });
    text += run.str + (run.eol ? '\n' : '');
  }
  return { text, spans };
}

/**
 * How wide each character is, relative to the others, in a typical proportional
 * font (Helvetica's metrics). A run's width is shared out in these proportions,
 * so the box for "081-234-5678" in "Tel 081-234-5678 ok" starts where the digits
 * do, not where an even split of the line would put it — an even split under-covers
 * the first digit of a number that follows a few narrow letters.
 */
const NARROW = new Set("il.,:;'|!".split(''));
const MEDIUM = new Set([...'()-[]{}/ tfrIJ', '\\']);
const WIDE = new Set('mwMW@%'.split(''));

export function charWeight(c: string): number {
  const code = c.charCodeAt(0);
  if (code >= 0x30 && code <= 0x39) {
    return 0.556; // digits are one width in nearly every font
  }
  if (code >= 0x0e00 && code <= 0x0e7f) {
    // Thai: combining vowels and tone marks sit on the previous letter and take no width of their own
    const combining = code === 0x0e31 || (code >= 0x0e34 && code <= 0x0e3a) || (code >= 0x0e47 && code <= 0x0e4e);
    return combining ? 0 : 0.5;
  }
  if (NARROW.has(c)) {
    return 0.25;
  }
  if (MEDIUM.has(c)) {
    return 0.32;
  }
  if (WIDE.has(c)) {
    return code >= 0x41 && code <= 0x5a ? 0.9 : 0.8;
  }
  if (code >= 0x41 && code <= 0x5a) {
    return 0.68;
  }
  return 0.53;
}

/**
 * The boxes covering `[start, end)` of a page's text: one per run it touches, cut
 * to the characters inside the range, then widened by a third of a character on
 * each side so a digit at the edge is covered rather than left showing. When the
 * whole run is inside the range it is covered whole.
 */
export function rectsOf(page: PageText, start: number, end: number): PdfRect[] {
  const rects: PdfRect[] = [];
  for (const { start: from, end: to, run } of page.spans) {
    const a = Math.max(start, from);
    const b = Math.min(end, to);
    if (b <= a || run.str.length === 0) {
      continue;
    }

    // where each character begins, as a share of the run's width
    const weights = Array.from(run.str, charWeight);
    const total = weights.reduce((sum, w) => sum + w, 0) || 1;
    const before = weights.slice(0, a - from).reduce((sum, w) => sum + w, 0);
    const inside = weights.slice(a - from, b - from).reduce((sum, w) => sum + w, 0);

    const whole = a === from && b === to;
    const average = (run.w * inside) / total / (b - a);
    const pad = whole ? 0 : average * 0.33;
    const x = run.x + (run.w * before) / total - pad;
    const w = (run.w * inside) / total + pad * 2;
    // the box reaches up through the cap height and a little below, for descenders
    rects.push({ x, y: run.y - run.h * 0.2, w, h: run.h * 1.2 });
  }
  return rects;
}

/** The runs on a pdf.js page, from its text-content items (the ones that carry a string). */
export function runsOf(items: readonly unknown[]): TextRun[] {
  return items
    .filter(
      (item): item is { str: string; transform: number[]; width: number; height: number; hasEOL?: boolean } =>
        !!item && typeof (item as { str?: unknown }).str === 'string'
    )
    .map((item) => ({ str: item.str, x: item.transform[4], y: item.transform[5], w: item.width, h: item.height, eol: item.hasEOL }));
}

/** Whether two boxes share any area. */
export function overlaps(a: PdfRect, b: PdfRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
